import { LegalPage } from "@/components/LegalPage";
import { LEGAL_UPDATED, useLegalLang } from "@/lib/legalLocale";
import {
  OPERATOR_NAME,
  PRODUCT_NAME,
  PRODUCT_NAME_ZH,
  PUBLIC_PRIVACY_URL,
  PUBLIC_TERMS_URL,
  SUPPORT_EMAIL,
} from "@/lib/publicLinks";

const linkClass = "text-foreground underline underline-offset-2 break-all";

export default function Privacy() {
  const lang = useLegalLang();
  return (
    <LegalPage title={lang === "en" ? "Privacy Policy" : "隱私權政策"}>
      {lang === "en" ? <PrivacyEn /> : <PrivacyZh />}
    </LegalPage>
  );
}

function PrivacyEn() {
  return (
    <>
      <header className="space-y-2">
        <p className="text-foreground font-medium">
          {PRODUCT_NAME} ({PRODUCT_NAME_ZH}) respects your privacy. This policy explains how we collect, use, store, and protect personal information, including under Taiwan’s Personal Data Protection Act.
        </p>
        <p>Last updated: {LEGAL_UPDATED}</p>
        <p>
          Public URL:{" "}
          <a className={linkClass} href={PUBLIC_PRIVACY_URL}>
            {PUBLIC_PRIVACY_URL}
          </a>
        </p>
      </header>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">1. Operator and contact</h2>
        <p>The service is provided by:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Operator: {OPERATOR_NAME}</li>
          <li>
            Email:{" "}
            <a className="text-foreground underline underline-offset-2" href={`mailto:${SUPPORT_EMAIL}`}>
              {SUPPORT_EMAIL}
            </a>
          </li>
        </ul>
        <p>Contact that address for privacy questions or to exercise your rights.</p>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">2. Scope, age, and connectivity</h2>
        <p>
          This policy covers the {PRODUCT_NAME} iOS app, related web pages, and the backend we use to run the service.
          An internet connection is required for sign-in, sync, exchange rates and quotes, and notifications.
        </p>
        <p>
          The service is for people aged <span className="text-foreground font-medium">13 and older</span>.
          Do not register or submit personal information if you are under 13. If we learn that we collected it by mistake, we will delete it.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">3. Purposes and data types</h2>
        <p>
          We process information to provide and maintain the service, authenticate you, respond to you, troubleshoot security and errors, comply with law, and run features you choose to use (such as reminders and AI parsing).
        </p>
        <p>Depending on the features you use, we may process:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            <span className="text-foreground font-medium">Account data</span>
            : email address, display name, and profile photo if you provide one or a sign-in provider supplies it.
          </li>
          <li>
            <span className="text-foreground font-medium">Financial data</span>
            : ledger entries, recurring entries, financial to-dos, accounts and balances, budgets, investment holdings and trades, savings jars, loans, exchange-rate preferences, shared-account and split-bill content, and text or voice you choose to import.
          </li>
          <li>
            <span className="text-foreground font-medium">Device and usage data</span>
            : device type, app version, and basic activity and error logs used to debug and improve the service. If you enable push notifications, this may include a push token.
          </li>
          <li>
            <span className="text-foreground font-medium">On-device preferences</span>
            : such as theme, language, default home screen, and some interface settings. These are not uploaded for marketing. Some of them may be removed when you sign out or clear local data.
          </li>
          <li>
            <span className="text-foreground font-medium">Photos</span>
            : only when you choose and upload a profile or account image. We do not scan your photo library in the background.
          </li>
          <li>
            <span className="text-foreground font-medium">Microphone, speech recognition, and camera</span>
            : only after you allow them for voice entry or for scanning a QR code to join a shared account. On iOS, speech is turned into text by Apple’s speech recognition. We do not access the microphone or camera without permission.
          </li>
          <li>
            <span className="text-foreground font-medium">Motion</span>
            : if you leave the budget-page water effect on, device motion may be used on the device for that visual. It is not used to build a marketing profile.
          </li>
        </ul>
        <p>Core bookkeeping does not ask for bank passwords or full card numbers.</p>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">4. How we use information</h2>
        <ul className="list-disc pl-5 space-y-1">
          <li>Create and maintain your account and provide ledger, asset, budget, investment, and loan features.</li>
          <li>Sync data across devices, build reports, and send service notices and reminders.</li>
          <li>Support shared accounts, invites, and split bills.</li>
          <li>Improve stability, security, and the product, including debugging.</li>
          <li>Reply to support and feedback.</li>
          <li>Comply with law.</li>
        </ul>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">5. Notifications</h2>
        <p>
          If you turn on daily reminders, budget alerts, or other notifications, we may use a push token and your settings to send them.
          You can turn notifications off in system settings or in the app. Some reminders will then stop.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">6. What we do not do</h2>
        <ul className="list-disc pl-5 space-y-1">
          <li>We do not sell your personal information.</li>
          <li>We do not use third-party advertising networks or ad-tracking SDKs, and we do not build cross-app profiles for personalized ads.</li>
          <li>We do not currently ship a separate marketing-analytics or attribution SDK. If we later add a tool for error monitoring or product improvement, we will update this policy.</li>
          <li>We do not let someone sign in to your account with only an email address and no password or third-party verification.</li>
        </ul>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">7. Sign-in</h2>
        <p>You can sign in with:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Sign in with Apple</li>
          <li>Google</li>
          <li>Email and password</li>
        </ul>
        <p>
          Authentication is handled by those providers. After a successful sign-in we create a session so the app can access your data.
          Signing out clears that session and related local sign-in state as far as we reasonably can.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">8. Service providers</h2>
        <p>We use the following providers only as needed to run the service:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            <span className="text-foreground font-medium">Authentication</span>
            : Supabase Auth, Apple, and Google.
          </li>
          <li>
            <span className="text-foreground font-medium">Email delivery</span>
            : Resend sends account verification and password-reset messages, so your email address is shared with Resend for that delivery.
          </li>
          <li>
            <span className="text-foreground font-medium">Hosting and database</span>
            : application servers and a managed database store and process your account and financial data.
          </li>
          <li>
            <span className="text-foreground font-medium">File storage</span>
            : files you upload, such as images.
          </li>
          <li>
            <span className="text-foreground font-medium">Market data</span>
            : exchange rates and security quotes may come from third-party sources and can be delayed or incomplete.
          </li>
          <li>
            <span className="text-foreground font-medium">AI</span>
            : if you use AI import, voice entry, or the new-user flow that builds a book from your description, the text is sent to Google Gemini to produce a draft. On iOS, speech is converted to text by Apple before that. Do not include secrets you do not want those services to process.
          </li>
        </ul>
        <p>Providers process data only as needed to perform their role and under their own privacy terms.</p>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">9. Shared accounts</h2>
        <p>
          If you create or join a shared account, record a split, or send an invite, the related ledger and account information can be visible to the other members.
          Share only with people you trust, and keep invite links private. We do not publish that information to unrelated third parties because you collaborated.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">10. Storage, security, and transfers</h2>
        <p>
          We use ordinary safeguards, including HTTPS and access controls. No online service can promise perfect security.
          Depending on our providers, data may be processed or backed up outside Taiwan. We expect those providers to use appropriate protections.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">11. Retention</h2>
        <p>
          We keep what we need while your account is active. When you clear data, delete the account, or we no longer need the information, we delete or de-identify it.
          Some backups, logs, or records required by law may remain for a limited time and then be removed.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">12. Your rights</h2>
        <p>Where the law allows, you may ask to:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>Access your information.</li>
          <li>Receive a copy, including through export.</li>
          <li>Correct it.</li>
          <li>Stop collection, processing, or use.</li>
          <li>Delete it, including with in-app account deletion.</li>
        </ul>
        <p>You can also do the following in the app:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            <span className="text-foreground font-medium">View and edit</span>
            : your profile in Settings.
          </li>
          <li>
            <span className="text-foreground font-medium">Export</span>
            : Settings → Data.
          </li>
          <li>
            <span className="text-foreground font-medium">Clear all data</span>
            : Settings → Data. This removes financial data in the account and keeps the login so you can continue.
          </li>
          <li>
            <span className="text-foreground font-medium">Delete account</span>
            : Settings → Data → Delete account, after several confirmations. This permanently deletes your sign-in identity, profile, ledger (including recurring entries and financial to-dos), budgets, assets, investments, savings jars, loans, reminders, notifications, and images you uploaded, and removes you from shared accounts. You are signed out immediately and this cannot be undone. Using the same email again requires a new registration.
          </li>
          <li>
            If you still own a shared account that has other active members, dissolve it, transfer ownership, or have the others leave before the account can be deleted. We do not delete data owned by other people.
          </li>
        </ul>
        <p>
          We may need to verify your identity before acting. If a request affects someone else, a legal duty, or cannot be completed technically, we will explain why.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">13. Children</h2>
        <p>
          The service is not directed to children under 13, and we do not knowingly collect their personal information.
          If you believe we have, contact us and we will delete it.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">14. Changes</h2>
        <p>
          We may update this policy. For material changes we will use an in-app notice, email, or another reasonable method.
          Continuing to use the service after an update means you have seen the new text. The latest version is at the public URL above.
        </p>
        <p>
          Terms of Service:{" "}
          <a className={linkClass} href={PUBLIC_TERMS_URL}>
            {PUBLIC_TERMS_URL}
          </a>
        </p>
      </section>
    </>
  );
}

function PrivacyZh() {
  return (
    <>
      <header className="space-y-2">
        <p className="text-foreground font-medium">
          {PRODUCT_NAME_ZH}（{PRODUCT_NAME}）重視您的隱私。本政策說明我們如何蒐集、使用、儲存與保護您的個人資料，並依中華民國《個人資料保護法》相關規定處理。
        </p>
        <p>最後更新日期：{LEGAL_UPDATED}</p>
        <p>
          公開網址：{" "}
          <a className={linkClass} href={PUBLIC_PRIVACY_URL}>
            {PUBLIC_PRIVACY_URL}
          </a>
        </p>
      </header>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">1. 營運者與聯絡方式</h2>
        <p>本服務由以下營運者提供（個人資料保護法所稱之「蒐集機關／保有利用機關」）：</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>營運者：{OPERATOR_NAME}</li>
          <li>
            聯絡信箱：{" "}
            <a className="text-foreground underline underline-offset-2" href={`mailto:${SUPPORT_EMAIL}`}>
              {SUPPORT_EMAIL}
            </a>
          </li>
        </ul>
        <p>若您對本政策或個資處理有任何疑問，或欲行使個資權利，請透過上述信箱與我們聯絡。</p>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">2. 適用範圍、年齡與連網需求</h2>
        <p>
          本政策適用於 {PRODUCT_NAME_ZH} 的 iOS App、相關網頁介面，以及我們為提供服務所使用的後端系統。
          本服務需要連線至網際網路，才能完成登入、同步資料、取得匯率／報價與接收通知等功能。
        </p>
        <p>
          本服務以
          <span className="text-foreground font-medium"> 13 歲以上</span>
          使用者為對象。未滿 13 歲者請勿註冊或提供個人資料；若我們發現誤收，將儘速刪除。
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">3. 蒐集目的與資料類型</h2>
        <p>
          蒐集目的主要包括：提供與維護本服務、身分驗證、客戶聯繫、資安與錯誤排查、法令遵循，以及經您同意之相關功能（例如推播提醒、AI 解析）。
        </p>
        <p>依您使用的功能，我們可能處理下列資料：</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            <span className="text-foreground font-medium">帳號資料</span>
            ：Email、顯示名稱、頭像（若您提供或由第三方登入帶入）。
          </li>
          <li>
            <span className="text-foreground font-medium">財務資料</span>
            ：記帳明細、週期性記帳、財務待辦、帳戶與餘額、預算、投資持股與交易、存錢罐、借貸、匯率偏好、共同帳戶／分帳相關內容，以及您主動匯入的文字／語音解析結果。
          </li>
          <li>
            <span className="text-foreground font-medium">裝置與使用資料</span>
            ：裝置類型、App 版本、基本操作與錯誤紀錄（用於除錯與改善服務）。若您啟用推播，可能包含推播權杖。
          </li>
          <li>
            <span className="text-foreground font-medium">裝置端偏好（多半僅存於您的裝置）</span>
            ：例如主題、語言、預設首頁、部分介面偏好。此類資料通常不會上傳作為行銷用途；登出或清除本機資料時，部分偏好可能一併移除。
          </li>
          <li>
            <span className="text-foreground font-medium">媒體與相簿</span>
            ：僅在您主動選擇並上傳頭像、帳戶圖片等時，才會存取您的相簿／照片並上傳所選檔案；我們不會在背景掃描您的相簿。
          </li>
          <li>
            <span className="text-foreground font-medium">語音、語音辨識與相機</span>
            ：僅在您使用語音記帳，或掃描 QR Code 加入共同帳戶並授權時處理。在 iOS 上，語音會先由 Apple 語音辨識轉成文字。我們不會在未授權時存取麥克風或相機。
          </li>
          <li>
            <span className="text-foreground font-medium">運動／感測（若您啟用相關視覺效果）</span>
            ：例如水流效果可能使用裝置姿態感測，通常僅在裝置端處理，不會因此建立行銷檔案。
          </li>
        </ul>
        <p>我們不會要求您提供銀行密碼、完整信用卡號等敏感付款憑證來使用本服務的核心記帳功能。</p>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">4. 我們如何使用資料</h2>
        <ul className="list-disc pl-5 space-y-1">
          <li>建立與維護您的帳號，並提供記帳、資產、預算、投資與借貸等功能。</li>
          <li>同步跨裝置資料、產生報表與分析、發送服務通知與提醒。</li>
          <li>支援共同帳戶、邀請與分帳協作。</li>
          <li>改善產品穩定度、安全性與使用體驗（含錯誤排查）。</li>
          <li>回覆您的客服／意見回饋請求。</li>
          <li>遵守適用法律或主管機關之要求。</li>
        </ul>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">5. 通知與推播</h2>
        <p>
          若您啟用每日提醒、預算提醒或其他推播／App 內通知，我們可能使用推播權杖與您的偏好設定來傳送相關訊息。
          您可隨時在系統設定或 App 設定中關閉推播；關閉後，部分提醒功能可能無法運作。
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">6. 我們不做的事</h2>
        <ul className="list-disc pl-5 space-y-1">
          <li>我們不會出售您的個人資料。</li>
          <li>我們目前不使用第三方廣告網路或廣告追蹤 SDK，也不會為了投放個人化廣告而建立跨 App 追蹤檔案。</li>
          <li>我們目前未部署獨立的行銷分析／歸因 SDK；若未來為錯誤監控或服務改善而導入工具，我們會更新本政策並在合理範圍內告知。</li>
          <li>我們不會以「僅輸入 Email、無需密碼或第三方驗證」的方式讓他人登入您的帳號。</li>
        </ul>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">7. 登入與身分驗證</h2>
        <p>您可透過下列方式登入：</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Sign in with Apple</li>
          <li>Google 登入</li>
          <li>Email 與密碼</li>
        </ul>
        <p>
          身分驗證由認證服務處理；成功後我們會建立本服務的工作階段（session）與必要的登入憑證儲存，以便安全存取您的資料。
          登出後，我們會清除本服務的工作階段，並盡力清除相關本機登入狀態。
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">8. 第三方服務</h2>
        <p>為提供服務，我們可能使用下列類型的第三方（在必要範圍內處理資料）：</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            <span className="text-foreground font-medium">身分驗證</span>
            ：Supabase Auth、Apple、Google。
          </li>
          <li>
            <span className="text-foreground font-medium">寄信</span>
            ：帳號驗證信與重設密碼信透過 Resend 寄出，因此您的 Email 會提供給 Resend 以完成寄送。
          </li>
          <li>
            <span className="text-foreground font-medium">雲端主機與資料庫</span>
            ：用於儲存與處理您的帳號與財務資料（例如應用程式伺服器與受管理的資料庫）。
          </li>
          <li>
            <span className="text-foreground font-medium">儲存空間</span>
            ：用於存放您上傳的圖片等檔案。
          </li>
          <li>
            <span className="text-foreground font-medium">市場資料</span>
            ：匯率或證券報價可能來自第三方資料來源；資料可能有延遲或不完整。
          </li>
          <li>
            <span className="text-foreground font-medium">AI</span>
            ：若您使用 AI 智慧匯入、語音記帳，或在新用戶引導中用描述建立帳本，相關文字會傳送至 Google Gemini 以產生草稿。在 iOS 上，語音會先由 Apple 語音辨識轉成文字，再送出該文字。請勿輸入您不希望這些服務處理的機密內容。
          </li>
        </ul>
        <p>第三方僅在提供服務所必要的範圍內處理資料，並受其各自隱私政策拘束。</p>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">9. 共同帳戶與分享</h2>
        <p>
          當您建立或加入共同帳戶、代墊／分帳，或邀請他人時，與該協作相關的記帳與帳戶資訊可能對共同成員可見。
          請僅與您信任的對象分享，並留意邀請連結的保管。我們不會因此取得權限去公開您的資料給無關第三方。
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">10. 資料儲存、安全與跨境傳輸</h2>
        <p>
          我們採用業界常見的安全措施，包括傳輸加密（HTTPS）與存取控管。請注意：任何網路服務都無法保證絕對安全。
          視雲端服務供應商之基礎設施，您的資料可能在臺灣以外地區處理或備份；我們會在合理範圍內要求供應商採取適當保護措施。
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">11. 資料保留</h2>
        <p>
          在您維持帳號期間，我們會保留提供服務所需的資料。當您清除資料、刪除帳號，或依法／依營運必要不再需要時，我們會刪除或匿名化相關資料；
          部分備份、日誌或法令要求之紀錄可能在合理期間內暫時保留後再清除。
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">12. 您的權利（含個資法）</h2>
        <p>在適用法律允許的範圍內，您得請求：</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>查詢或請求閱覽。</li>
          <li>請求製給複製本（例如透過匯出功能）。</li>
          <li>請求補充或更正。</li>
          <li>請求停止蒐集、處理或利用。</li>
          <li>請求刪除（包含透過 App 內「刪除帳號」）。</li>
        </ul>
        <p>您也可直接在 App 內行使下列操作：</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            <span className="text-foreground font-medium">查閱與更正</span>
            ：可在設定中的個人檔案檢視與更新。
          </li>
          <li>
            <span className="text-foreground font-medium">匯出</span>
            ：可在「設定 → 資料管理」匯出資料。
          </li>
          <li>
            <span className="text-foreground font-medium">清除所有資料</span>
            ：可在「設定 → 資料管理」執行。會清除帳號內財務資料，但登入帳號保留，您仍可繼續使用本服務。
          </li>
          <li>
            <span className="text-foreground font-medium">刪除帳號</span>
            ：可在「設定 → 資料管理 → 刪除帳號」執行（需多次確認）。將永久刪除您的登入身分、個人資料、記帳（含週期性記帳與財務待辦）／預算／資產／投資／存錢罐／借貸、提醒與通知，以及您上傳的圖片；並移除您在共同帳戶中的成員身分。刪除後會立即登出且無法復原；若使用相同 Email 再次使用，需重新註冊。
          </li>
          <li>
            若您仍是共同帳戶的擁有者且尚有其他活躍成員，請先解散帳戶、轉移擁有權，或請其他成員離開後，才能完成帳號刪除。我們不會刪除其他用戶擁有的資料。
          </li>
        </ul>
        <p>
          我們可能依法要求您配合身分驗證後再處理請求；若請求影響第三人權利、法令義務或技術上無法完成，我們會說明理由。
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">13. 兒童隱私</h2>
        <p>
          本服務並非以未滿 13 歲之兒童為對象，亦不會明知而蒐集其個人資料。
          若您認為我們誤收兒童個人資料，請聯絡我們，我們將儘速處理與刪除。
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">14. 政策更新</h2>
        <p>
          我們可能更新本政策。重大變更時，我們會透過 App 內提示、Email 或其他合理方式告知。
          更新後若您繼續使用本服務，即表示您知悉更新後的內容。最新版本亦公布於上述公開網址。
        </p>
        <p>
          服務條款請見：{" "}
          <a className={linkClass} href={PUBLIC_TERMS_URL}>
            {PUBLIC_TERMS_URL}
          </a>
        </p>
      </section>
    </>
  );
}
